// labelText.ts — Rasterize a short label to a cached canvas texture for use on a
// billboarded <sprite>. This is the cheap alternative to drei <Html> for the
// galaxy's per-star names: a sprite is positioned and turned to face the camera
// ON THE GPU, with no DOM node and no per-frame CPU reprojection, so hundreds of
// always-on labels cost almost nothing (220 <Html> labels halved the framerate).
import { CanvasTexture, SRGBColorSpace, type Texture } from 'three';

export interface Label {
  texture: Texture;
  /** width / height of the rasterized text, to keep the sprite's aspect right. */
  aspect: number;
}

const FONT_PX = 48;
const PAD = 10;
// Full canvas height ÷ text height: the rasterized label is taller than the glyphs by
// the padding on each side, so a constant-size sprite must account for it to land the
// TEXT at a target pixel height. Consumed by StarMarker's constant-px label scale.
export const LABEL_CANVAS_RATIO = (FONT_PX + 2 * PAD) / FONT_PX;
const cache = new Map<string, Label>();

export function labelText(text: string): Label {
  const hit = cache.get(text);
  if (hit) {
    return hit;
  }
  const font = `500 ${FONT_PX}px "Fira Code", ui-monospace, monospace`;
  const measureCtx = document.createElement('canvas').getContext('2d');
  let textW = text.length * FONT_PX * 0.62; // fallback estimate
  if (measureCtx) {
    measureCtx.font = font;
    textW = measureCtx.measureText(text).width;
  }
  const w = Math.ceil(textW) + PAD * 2;
  const h = FONT_PX + PAD * 2;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // A soft dark halo so names stay legible over bright stars and nebulae.
    ctx.shadowColor = 'rgba(0,0,0,0.95)';
    ctx.shadowBlur = 7;
    ctx.fillStyle = '#eaf0ff';
    ctx.fillText(text, w / 2, h / 2);
    ctx.fillText(text, w / 2, h / 2); // twice = denser halo
  }
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 4;
  const label: Label = { texture, aspect: w / h };
  cache.set(text, label);
  return label;
}
